/**
 * useMarginReport Hook
 *
 * React Query hook for fetching margin report data.
 * Also provides SSE-based version with progress tracking.
 * @see US-25d.14 - Hook useMarginReport
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { marginReportService } from '../services/margin-report.service';
import { useSSE } from '@/hooks';
import type {
  MarginReportInput,
  MarginReportResponse,
  SSEProgressEvent,
  MarginReportStep,
} from '@bstock/shared';
import { MarginReportStepLabels } from '@bstock/shared';

/**
 * Hook to fetch margin report data (standard version)
 *
 * @param params - Date range parameters (dateFrom, dateTo as ISO strings)
 * @returns Query result with report data, loading state, and error
 */
export function useMarginReport(params: MarginReportInput | null) {
  return useQuery<MarginReportResponse>({
    queryKey: ['margin-report', params?.dateFrom, params?.dateTo],
    queryFn: () => marginReportService.getReport(params!),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    enabled: Boolean(params?.dateFrom && params?.dateTo),
  });
}

/**
 * Result type for the SSE-based margin report hook
 */
export interface UseMarginReportWithProgressResult {
  /** Start generating the report */
  generate: () => void;
  /** Cancel the report generation */
  cancel: () => void;
  /** Current progress event */
  progress: SSEProgressEvent | null;
  /** Current step label for display */
  stepLabel: string | null;
  /** Final report data (available after completion) */
  data: MarginReportResponse | null;
  /** Error message if generation failed */
  error: string | null;
  /** Whether generation is in progress */
  isLoading: boolean;
}

/**
 * SSE-based hook for margin report with real-time progress
 *
 * @param params - Date range parameters (dateFrom, dateTo as ISO strings)
 * @returns Progress state and control functions
 */
export function useMarginReportWithProgress(
  params: MarginReportInput | null
): UseMarginReportWithProgressResult {
  // Store the last successful result to avoid flicker when regenerating
  const [cachedData, setCachedData] = useState<MarginReportResponse | null>(null);

  // Build the SSE URL with query params
  const sseUrl = useMemo(() => {
    if (!params?.dateFrom || !params?.dateTo) return '';
    return `/reports/margin/stream?dateFrom=${encodeURIComponent(params.dateFrom)}&dateTo=${encodeURIComponent(params.dateTo)}`;
  }, [params?.dateFrom, params?.dateTo]);

  // Track params changes to detect if we need to regenerate
  const paramsRef = useRef(params);
  const shouldAutoGenerate = useRef(false);

  const { start, stop, progress, result, error, isLoading } = useSSE<MarginReportResponse>(
    sseUrl,
    {
      onDone: (reportResult) => {
        setCachedData(reportResult);
      },
    }
  );

  // Get step label from current progress
  const stepLabel = useMemo(() => {
    if (!progress?.step) return null;
    const step = progress.step as MarginReportStep;
    return MarginReportStepLabels[step] || progress.step;
  }, [progress?.step]);

  // Generate function that validates params first
  const generate = useCallback(() => {
    if (!params?.dateFrom || !params?.dateTo) {
      return;
    }
    // Clear cached data when starting new generation
    setCachedData(null);
    start();
  }, [params?.dateFrom, params?.dateTo, start]);

  // Auto-generate when params change (after initial load)
  useEffect(() => {
    if (
      params?.dateFrom &&
      params?.dateTo &&
      (paramsRef.current?.dateFrom !== params.dateFrom ||
        paramsRef.current?.dateTo !== params.dateTo)
    ) {
      paramsRef.current = params;
      // Only auto-generate after the first manual trigger
      if (shouldAutoGenerate.current) {
        generate();
      }
    }
  }, [params?.dateFrom, params?.dateTo, generate]);

  // Mark that we should auto-generate on subsequent param changes
  const wrappedGenerate = useCallback(() => {
    shouldAutoGenerate.current = true;
    generate();
  }, [generate]);

  return {
    generate: wrappedGenerate,
    cancel: stop,
    progress,
    stepLabel,
    data: result || cachedData,
    error,
    isLoading,
  };
}
